// DP252 — the list rules the Tasks page and My Desk both read, in one place.
import { useEffect, useState } from 'react';
import { parseDay, DEFAULT_CALENDAR } from './workingDays';
import { getCompanySettings, peekCompanySettings, subscribeCompanySettings } from './companySettings';

export const Q_ORDER = ['now', 'sched', 'del', 'later'];
export const qRank = (q) => (q ? Q_ORDER.indexOf(q) : Q_ORDER.length); // no quadrant sits last

// DP246 — what I hold, and what I handed out but still have to chase.
export const mineOf = (t, me) => (t.assignee_id === me || (!t.assignee_id && (t.assigned_to === me || t.created_by === me)));
export const handedOutOf = (t, me) => (t.created_by === me && !!t.assignee_id && t.assignee_id !== me);

export const openOf = (t) => t.status !== 'completed' && !t.archived_at;
export const lateOf = (t, today) => { const d = parseDay(t.due_date); return !!d && d < today; };
export const isTodayOf = (t, today) => { const d = parseDay(t.due_date); return !!d && d.getTime() === today.getTime(); };

// TK-3 — the focus strip's state, kept per user in the browser.
export const focusKey = (me) => `kmt-focus:${me || 'anon'}`;
export const readFocus = (me) => {
  try {
    const v = JSON.parse(window.localStorage.getItem(focusKey(me)) || 'null');
    return v && v.taskId ? v : null;
  } catch { return null; }
};
export const writeFocus = (me, v) => {
  try {
    if (v) window.localStorage.setItem(focusKey(me), JSON.stringify(v));
    else window.localStorage.removeItem(focusKey(me));
  } catch { /* a private window is not a reason to stop working */ }
};

// TK-1b — one working day, until Admin sets it; and what an unestimated task costs.
export const DAY_MINUTES = 480;
export const DEFAULT_ESTIMATE = 30;
export const estOf = (t) => (Number.isFinite(Number(t.estimate_minutes)) && Number(t.estimate_minutes) > 0 ? Number(t.estimate_minutes) : DEFAULT_ESTIMATE);
export const plannedMinutes = (list) => list.filter(openOf).reduce((n, t) => n + estOf(t), 0);

// TK-5 — working hours for the free-slot search, until Admin sets them.
export const DAY_START_HOUR = 8;
export const DAY_END_HOUR = 17;
export const fmtClock = (iso) => {
  const d = new Date(iso);
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ap = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${h}:${m} ${ap}`;
};
export const localStamp = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

// The first gaps in the working day that fit `minutes`, never in the past.
// AD-1 — the hours come from the firm's Working Calendar; the defaults above
// are only what a firm that never set them gets.
export function freeSlots(events, days, minutes, now, hours = { start: '08:00', end: '17:00' }) {
  const at = (day, hhmm, fallbackHour) => {
    const [h, m] = String(hhmm || '').split(':').map(Number);
    const d = new Date(day);
    d.setHours(Number.isFinite(h) ? h : fallbackHour, Number.isFinite(m) ? m : 0, 0, 0);
    return d;
  };
  const out = [];
  for (const day of days) {
    const dayStart = at(day, hours?.start, DAY_START_HOUR);
    const dayEnd = at(day, hours?.end, DAY_END_HOUR);
    let cursor = dayStart < now ? new Date(Math.ceil(now.getTime() / 900000) * 900000) : dayStart;
    const busy = events
      .filter(e => !e.all_day && paddedSpan(e).start < dayEnd && paddedSpan(e).end > dayStart)
      .map(e => ({ s: paddedSpan(e).start, e: paddedSpan(e).end, t: e.title }))
      .sort((a, b) => a.s - b.s);
    let last = null;
    for (const b of [...busy, { s: dayEnd, e: dayEnd, t: null }]) {
      if (b.s.getTime() - cursor.getTime() >= minutes * 60000) {
        out.push({
          start: new Date(cursor),
          end: new Date(cursor.getTime() + minutes * 60000),
          after: last ? `after ${last}` : 'start of the day',
        });
      }
      if (b.e > cursor) cursor = new Date(b.e);
      last = b.t;
      if (out.length >= 6) return out;
    }
  }
  return out;
}

// TK-11 — travel is part of the meeting: the drive there (plus arriving early)
// in front, the drive home behind the last stop of the day. Nothing is written
// to Google and no separate block exists — the event simply occupies more.
const travelCache = new Map();   // (location | local hour) → estimate, for the page's life

export const paddedSpan = (e) => ({
  start: new Date(new Date(e.start).getTime() - (e.travel?.lead || 0) * 60000),
  end: new Date(new Date(e.end).getTime() + (e.travel?.tail || 0) * 60000),
});

export async function padEventsWithTravel(events, api) {
  let settings = null;
  try {
    const res = await api.get('/travel/settings');
    settings = res?.data || null;
  } catch {
    return { events, travel: null };
  }
  if (!settings?.key_set || !settings?.start) return { events, travel: null };

  const isCandidate = (e) => !e.all_day && !e.is_block && String(e.location || '').trim();
  const candidates = events.filter(isCandidate)
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));
  if (candidates.length === 0) return { events, travel: settings };

  const estimateFor = async (e) => {
    const at = new Date(e.start);
    const key = `${String(e.location).trim().toLowerCase()}|${at.getHours()}`;
    if (!travelCache.has(key)) {
      try {
        const res = await api.get('/travel/estimate', { params: { to: e.location, at: at.toISOString() } });
        travelCache.set(key, res?.data?.minutes ? res.data : null);
      } catch {
        travelCache.set(key, null);
      }
    }
    return travelCache.get(key);
  };

  const dayOf = (e) => String(localStamp(new Date(e.start))).slice(0, 10);
  const lastOfDay = new Map();
  candidates.forEach((e) => lastOfDay.set(dayOf(e), e.id));

  const pad = new Map();
  let prevEnd = null;
  let prevDay = null;
  for (const e of candidates) {
    const est = await estimateFor(e);      // eslint-disable-line no-await-in-loop
    const day = dayOf(e);
    if (day !== prevDay) { prevEnd = null; prevDay = day; }
    if (est) {
      const want = est.minutes + (settings.buffer_minutes || 0);
      // Travel between two stops belongs to the second stop's lead, and only
      // the time that is actually free: no double counting.
      const room = prevEnd ? Math.max(0, Math.round((new Date(e.start) - prevEnd) / 60000)) : want;
      const lead = Math.min(want, room);
      const tail = (settings.return_trip !== false && lastOfDay.get(day) === e.id) ? est.minutes : 0;
      pad.set(e.id, { lead, tail, minutes: est.minutes, distance_miles: est.distance_miles });
    }
    prevEnd = new Date(new Date(e.end).getTime() + ((pad.get(e.id)?.tail) || 0) * 60000);
  }

  return {
    events: events.map(e => (pad.has(e.id) ? { ...e, travel: pad.get(e.id) } : e)),
    travel: settings,
  };
}

// TK-4 — the lead: how many days before its date a task starts asking.
export const leadOf = (t) => (Number.isFinite(Number(t.lead_days)) && t.lead_days !== null && t.lead_days !== ''
  ? Number(t.lead_days)
  : (estOf(t) < 60 ? 2 : estOf(t) <= 240 ? 5 : 10));
export const daysUntil = (t, today) => {
  const d = parseDay(t.due_date);
  return d ? Math.round((d.getTime() - today.getTime()) / 864e5) : null;
};
export const comingUpOf = (tasks, me, today) => tasks.filter((t) => {
  if (!openOf(t) || t.inbox) return false;
  if (!(mineOf(t, me) || handedOutOf(t, me))) return false;
  const n = daysUntil(t, today);
  return n !== null && n > 0 && n <= leadOf(t);
}).sort((a, b) => daysUntil(a, today) - daysUntil(b, today));

// TK-1a — a time estimate read the way a person says it: 15 m, 1 h, 1 h 30.
export const fmtMinutes = (n) => {
  const m = Number(n);
  if (!m || m <= 0) return '';
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r} m`;
  return r ? `${h} h ${r}` : `${h} h`;
};

export const dayKeyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const todayListOf = (tasks, me, today) => tasks.filter(t => (
  openOf(t) && !t.inbox && (mineOf(t, me) || handedOutOf(t, me)) && (isTodayOf(t, today) || lateOf(t, today))
));

export const doneTodayOf = (tasks, today) => tasks.filter(t => (
  t.status === 'completed' && String(t.completed_at || t.updated_at || '').slice(0, 10) === dayKeyOf(today)
));

// TK-10 — the firm's working days, from Admin, with the default until the
// session has been told. companySettings holds one answer per session.
// AD-1 — and the firm's working hours and day length, from the same record.
const DEFAULT_HOURS = { day_start: '08:00', day_end: '17:00', day_minutes: DAY_MINUTES };
const spanMinutes = (start, end) => {
  const [a, b] = [start, end].map((t) => {
    const [h, m] = String(t).split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  });
  return Math.max(0, b - a);
};

export function useFirmCalendar() {
  const [cal, setCal] = useState(() => calFrom(peekCompanySettings()));
  useEffect(() => {
    let off = false;
    getCompanySettings().then(v => { if (!off) setCal(calFrom(v)); }).catch(() => {});
    const stop = subscribeCompanySettings(v => setCal(calFrom(v)));
    return () => { off = true; stop(); };
  }, []);
  return cal;
}

function calFrom(settings) {
  if (!settings) return { ...DEFAULT_CALENDAR, ...DEFAULT_HOURS };
  const week = settings.work_week || settings.working_days;
  // AD-1 — the firm's hours, and the minutes of task work a day can hold.
  const start = /^([01]\d|2[0-3]):[0-5]\d$/.test(String(settings.work_day_start || '')) ? settings.work_day_start : DEFAULT_HOURS.day_start;
  const end = /^([01]\d|2[0-3]):[0-5]\d$/.test(String(settings.work_day_end || '')) ? settings.work_day_end : DEFAULT_HOURS.day_end;
  const span = spanMinutes(start, end);
  const set = Number(settings.day_plan_minutes);
  return {
    work_week: Array.isArray(week) && week.length ? week : DEFAULT_CALENDAR.work_week,
    holidays: Array.isArray(settings.holidays) ? settings.holidays : DEFAULT_CALENDAR.holidays,
    day_start: start,
    day_end: end,
    day_minutes: settings.day_plan_minutes !== null && settings.day_plan_minutes !== undefined && Number.isFinite(set) && set > 0
      ? set
      : (span > 0 ? span : DAY_MINUTES),
  };
}

// TK-8 — opening and closing the day. Each sheet is shown once a day, and
// what the browser remembers is only THAT it was shown, never a decision.
export const ritualKey = (me, kind, dayKey) => `kmt-ritual:${me || 'anon'}:${kind}:${dayKey}`;
export const ritualDone = (me, kind, dayKey) => {
  try { return !!window.localStorage.getItem(ritualKey(me, kind, dayKey)); } catch { return false; }
};
export const markRitual = (me, kind, dayKey, how) => {
  try { window.localStorage.setItem(ritualKey(me, kind, dayKey), how); } catch { /* private window */ }
};

export const carriedOverOf = (tasks, me, today) => tasks
  .filter(t => openOf(t) && !t.inbox && (mineOf(t, me) || handedOutOf(t, me)) && lateOf(t, today))
  .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));

export const leftTodayOf = (tasks, me, today) => tasks
  .filter(t => openOf(t) && !t.inbox && (mineOf(t, me) || handedOutOf(t, me)) && isTodayOf(t, today));
